const PRICE = "filter.v.price";
const STOCK = "filter.v.availability";

function validateFilters(filters, definitions) {
  const known = new Map(
    definitions.map((definition) => [definition.paramName, definition]),
  );
  for (const definition of definitions) {
    if (
      definition.type !== "price_range" &&
      !["OR", "AND"].includes(definition.operator)
    ) {
      throw new Error("Unsupported facet operator");
    }
  }
  for (const [name, values] of Object.entries(filters.values ?? {})) {
    const supported =
      name === STOCK ||
      /^filter\.v\.option\.[^.]+$/.test(name) ||
      /^filter\.p\.m\.[^.]+\.[^.]+$/.test(name) ||
      ["filter.p.vendor", "filter.p.product_type", "filter.p.tag"].includes(
        name,
      );
    if (values.length && (!known.has(name) || !supported))
      throw new Error("Unknown facet");
  }
}

function matchingEntries(
  family,
  filters,
  definitions,
  ignoreFacet,
  technical = false,
) {
  validateFilters(filters, definitions);
  for (const definition of definitions) {
    const name = definition.paramName;
    const selected = filters.values?.[name] ?? [];
    if (
      name === ignoreFacet ||
      name === STOCK ||
      name === PRICE ||
      !selected.length
    )
      continue;
    const actual = family.facets?.[name] ?? family.options?.[name] ?? [];
    if (
      !Array.isArray(actual) ||
      actual.some((value) => typeof value !== "string")
    )
      throw new Error("Unsupported facet values");
    const matches = (value) => actual.includes(value);
    if (
      !(definition.operator === "AND"
        ? selected.every(matches)
        : selected.some(matches))
    )
      return [];
  }
  return (technical ? family.technicalEntries : family.entries).filter(
    (entry) => {
      if (
        ignoreFacet !== PRICE &&
        ((filters.minPrice != null && entry.price < filters.minPrice) ||
          (filters.maxPrice != null && entry.price > filters.maxPrice))
      )
        return false;
      const selectedStock =
        ignoreFacet === STOCK ? [] : (filters.values?.[STOCK] ?? []);
      return (
        !selectedStock.length ||
        selectedStock.includes(entry.available ? "1" : "0")
      );
    },
  );
}

export function familyFacetMatches(family, filters, definitions, ignoreFacet) {
  return matchingEntries(family, filters, definitions, ignoreFacet).length > 0;
}

export function correctFamilyFacetCounts(
  nativeCounts,
  families,
  filters,
  definitions,
) {
  return nativeCounts.map((definition) => {
    if (definition.type === "price_range") {
      const entries = families.flatMap((family) =>
        matchingEntries(family, filters, definitions, PRICE),
      );
      const highest = entries.reduce(
        (max, entry) => (!max || entry.price > max.price ? entry : max),
        null,
      );
      return {
        ...definition,
        rangeMax: Math.max(definition.rangeMax ?? 0, highest?.price ?? 0),
        rangeMaxFormatted:
          highest && highest.price > (definition.rangeMax ?? 0)
            ? highest.formattedPrice
            : definition.rangeMaxFormatted,
      };
    }
    return {
      ...definition,
      values: definition.values.map((value) => {
        const candidate = {
          ...filters,
          values: { ...filters.values, [definition.paramName]: [value.value] },
        };
        const delta = families.reduce(
          (sum, family) =>
            sum +
            Number(matchingEntries(family, candidate, definitions).length > 0) -
            Number(
              matchingEntries(family, candidate, definitions, undefined, true)
                .length > 0,
            ),
          0,
        );
        const count = value.count + delta;
        if (!Number.isSafeInteger(count) || count < 0)
          throw new Error("Inconsistent facet count");
        return { ...value, count };
      }),
    };
  });
}

const itemKey = (item) => `${item.objectType}:${item.id}`;

export function mergeGeneralResults(
  nativeItems,
  baseItems,
  families,
  filters,
  sort,
  definitions,
) {
  const baseRanks = new Map(
    baseItems.map((item, index) => [itemKey(item), index]),
  );
  const familyById = new Map(
    families.map((family) => [String(family.familyId), family]),
  );
  const output = nativeItems.filter((item) => !familyById.has(String(item.id)));
  if (output.some((item) => !baseRanks.has(itemKey(item))))
    throw new Error("Inconsistent native context");
  const familyItems = baseItems
    .filter(
      (item) =>
        item.objectType === "product" && familyById.has(String(item.id)),
    )
    .flatMap((item) => {
      const family = familyById.get(String(item.id));
      const entries = matchingEntries(family, filters, definitions);
      return entries.length
        ? [
            {
              ...item,
              family,
              matchingEntries: entries,
              price: Math.min(...entries.map((entry) => entry.price)),
            },
          ]
        : [];
    });
  const priceSort = sort === "price-ascending" || sort === "price-descending";
  if (priceSort)
    familyItems.sort(
      (a, b) =>
        (a.price - b.price) * (sort === "price-descending" ? -1 : 1) ||
        baseRanks.get(itemKey(a)) - baseRanks.get(itemKey(b)),
    );
  for (const family of familyItems) {
    const rank = baseRanks.get(itemKey(family));
    const before = output.findIndex((item) => {
      if (!priceSort || item.objectType !== "product")
        return baseRanks.get(itemKey(item)) > rank;
      const difference =
        (item.price - family.price) * (sort === "price-descending" ? -1 : 1);
      return (
        difference > 0 ||
        (difference === 0 && baseRanks.get(itemKey(item)) > rank)
      );
    });
    output.splice(before < 0 ? output.length : before, 0, family);
  }
  const keys = output.map(itemKey);
  if (new Set(keys).size !== keys.length)
    throw new Error("Duplicate native item");
  return output;
}

export function baseCatalogUrl(input) {
  const url = new URL(input);
  url.hash = "";
  for (const key of [...url.searchParams.keys()])
    if (key.startsWith("filter.") || key === "page")
      url.searchParams.delete(key);
  if (
    ["price-ascending", "price-descending"].includes(
      url.searchParams.get("sort_by"),
    )
  )
    url.searchParams.delete("sort_by");
  return url;
}
export function decodeCatalogFilters(url) {
  const values = {};
  for (const name of new Set(url.searchParams.keys())) {
    if (
      name.startsWith(`${PRICE}.`) &&
      ![`${PRICE}.gte`, `${PRICE}.lte`].includes(name)
    )
      throw new Error("Unknown price facet");
    if (name.startsWith("filter.") && !name.startsWith(`${PRICE}.`))
      values[name] = url.searchParams.getAll(name);
  }
  const amount = (suffix) => {
    const value = url.searchParams.get(`${PRICE}.${suffix}`);
    if (value == null || value === "") return null;
    if (!/^\d+(\.\d{1,2})?$/.test(value))
      throw new Error("Invalid price facet");
    const minor = Math.round(Number(value) * 100);
    if (!Number.isSafeInteger(minor)) throw new Error("Invalid price");
    return minor;
  };
  return { values, minPrice: amount("gte"), maxPrice: amount("lte") };
}
export function assertCompleteFamily(family) {
  if (
    !family.children ||
    family.children.length !== 6 ||
    new Set(family.children.map((child) => child.id)).size !== 6
  )
    throw new Error("Incomplete family");
  for (const child of family.children) {
    const entries = family.entries.filter(
      (entry) => entry.productId === child.id,
    );
    if (
      !Number.isSafeInteger(child.expected) ||
      child.expected < 1 ||
      child.expected > 250 ||
      entries.length !== child.expected ||
      new Set(entries.map((entry) => entry.variantId)).size !== child.expected
    )
      throw new Error("Incomplete family variants");
  }
  if (
    family.entries.some(
      (entry) => !family.children.some((child) => child.id === entry.productId),
    )
  )
    throw new Error("Incomplete family relation");
}
export async function collectCatalogStream(
  input,
  { readPage, signal, loadFamily },
) {
  const start = new URL(input);
  const context = (url) => {
    const clean = new URL(url);
    clean.searchParams.delete("page");
    clean.searchParams.sort();
    return clean.href;
  };
  let url = start,
    expectedPage = 1,
    total;
  const visited = new Set(),
    keys = new Set(),
    items = [],
    families = [];
  let first;
  while (url) {
    signal?.throwIfAborted();
    if (
      visited.size >= 50 ||
      url.origin !== start.origin ||
      context(url) !== context(start) ||
      visited.has(url.href)
    )
      throw new Error("Invalid pagination");
    visited.add(url.href);
    const page = await readPage(url);
    first ??= page;
    total ??= page.total;
    if (start.pathname.endsWith("/search") && total >= 1000)
      throw new Error("Search limit");
    if (
      !Number.isSafeInteger(total) ||
      total < 0 ||
      page.total !== total ||
      page.currentPage !== expectedPage++ ||
      page.perPage < 1
    )
      throw new Error("Incomplete pagination");
    for (const item of page.items) {
      const key = itemKey(item);
      if (keys.has(key)) throw new Error("Duplicate native item");
      keys.add(key);
      items.push(item);
    }
    for (const metadata of page.families) {
      const family = loadFamily ? await loadFamily(metadata, url) : metadata;
      assertCompleteFamily(family);
      families.push(family);
    }
    url = page.next ? new URL(page.next, start) : null;
    if (url && start.searchParams.has("section_id")) {
      if (
        url.searchParams.has("section_id") &&
        url.searchParams.get("section_id") !==
          start.searchParams.get("section_id")
      )
        throw new Error("Invalid pagination section");
      url.searchParams.set("section_id", start.searchParams.get("section_id"));
    }
  }
  if (items.length !== total) throw new Error("Incomplete native stream");
  return { ...first, items, families };
}

function parseNativePage(text) {
  const html = new DOMParser().parseFromString(text, "text/html");
  const metadata = html.querySelector("[data-general-index]");
  if (!metadata) throw new Error("Missing native index");
  const page = JSON.parse(metadata.textContent);
  page.html = html;
  page.items = [...html.querySelectorAll("[data-general-id]")].map((node) => ({
    id: node.dataset.generalId,
    objectType: node.dataset.generalType,
    price: Number(node.dataset.generalPrice),
    card: node,
  }));
  page.families = page.families.map(({ selection, ...native }) => ({
    ...selection,
    ...native,
    facets: Object.fromEntries(
      Object.entries(native.facets).map(([key, value]) => [
        key,
        value == null ? [] : Array.isArray(value) ? value : [value],
      ]),
    ),
  }));
  return page;
}

export function catalogReadBudget({
  signal,
  fetcher = fetch,
  now = () => performance.now(),
  maxBytes = 10 * 1024 * 1024,
  maxRequests = 50,
  timeoutMs = 20000,
}) {
  const started = now();
  const metrics = { requests: 0, bytes: 0, durationMs: 0 };
  return {
    metrics,
    async read(url) {
      signal.throwIfAborted();
      if (++metrics.requests > maxRequests || now() - started >= timeoutMs)
        throw new Error("Catalog budget");
      const response = await fetcher(url, { signal });
      if (!response.ok) throw new Error("Native catalog response");
      const text = await response.text();
      metrics.bytes += new TextEncoder().encode(text).byteLength;
      metrics.durationMs = Math.round(now() - started);
      if (metrics.bytes > maxBytes || metrics.durationMs >= timeoutMs)
        throw new Error("Catalog budget");
      return text;
    },
  };
}

function correctFacetDom(root, counts, resultCount, currency, locale) {
  for (const definition of counts) {
    if (definition.type === "price_range") {
      for (const node of root.querySelectorAll(
        'input[name="filter.v.price.gte"],input[name="filter.v.price.lte"]',
      )) {
        node.dataset.max = String(definition.rangeMax / 100);
        if (node.name.endsWith(".lte"))
          node.placeholder = String(definition.rangeMax / 100);
      }
      const money = definition.rangeMaxFormatted
        ? new DOMParser().parseFromString(
            definition.rangeMaxFormatted,
            "text/html",
          ).body.textContent
        : new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
          }).format(definition.rangeMax / 100);
      for (const label of root.querySelectorAll("[data-general-max-price]"))
        label.textContent = label.dataset.priceTemplate.replace(
          "__PRICE__",
          money,
        );
      continue;
    }
    for (const input of root.querySelectorAll('input[type="checkbox"]')) {
      if (input.name !== definition.paramName) continue;
      const value = definition.values.find(
        (value) => value.value === input.value,
      );
      if (!value) throw new Error("Incomplete facet controls");
      input.disabled = value.count === 0 && !input.checked;
      const label =
        input.closest("label") ??
        root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      label?.classList.toggle("disabled", input.disabled);
      const count = label?.querySelector(".facet-checkbox__count");
      if (count) count.textContent = ` (${value.count})`;
      const accessible = label?.querySelector(".visually-hidden");
      if (accessible)
        accessible.textContent = `${label.querySelector(".facet-checkbox__text-label")?.textContent ?? input.value}, ${value.count}`;
    }
  }
  for (const node of root.querySelectorAll(
    "#ProductCount,#ProductCountDesktop,.mobile-facets__count",
  )) {
    node.textContent = `${resultCount}`;
    node.dataset.productCount = String(resultCount);
    node.dataset.totalCount = String(resultCount);
  }
}

function updateFamilyCard(item) {
  const card = item.card.cloneNode(true);
  const family = card.querySelector("[data-family-card]");
  if (!family) throw new Error("Missing family card");
  const entry = item.matchingEntries.reduce((best, entry) =>
    entry.price < best.price ? entry : best,
  );
  const anchor = family.querySelector("a");
  const url = new URL(anchor.href, location.origin);
  url.search = "";
  url.hash = `${entry.productHandle}/${entry.variantId}`;
  family.querySelectorAll("a").forEach((link) => (link.href = url.href));
  const price = family.querySelector("[data-card-price]");
  if (typeof entry.formattedPrice !== "string")
    throw new Error("Missing native price format");
  const formattedPrice = new DOMParser().parseFromString(
    entry.formattedPrice,
    "text/html",
  ).body.textContent;
  price.textContent = `${family.dataset.priceFrom} ${formattedPrice}`;
  const availability = family.querySelector("[data-card-availability]");
  availability.textContent = item.matchingEntries.some(
    (entry) => entry.available,
  )
    ? family.dataset.available
    : family.dataset.soldOut;
  if (!entry.image?.src || !entry.mediaId)
    throw new Error("Missing family image");
  const image = document.createElement("img");
  image.src = entry.image.src;
  image.width = entry.image.width;
  image.height = entry.image.height;
  image.alt = entry.image.alt ?? "";
  image.loading = "lazy";
  image.sizes = "(min-width: 750px) 40vw, 90vw";
  family.querySelector("[data-card-image]").replaceChildren(image);
  return card;
}

function hydrateFacetControls(
  root,
  url,
  filters,
  definitions,
  nativeHtml,
  selectedLabel,
) {
  for (const input of root.querySelectorAll(
    'input[name],select[name="sort_by"]',
  )) {
    if (input.type === "checkbox" && input.name.startsWith("filter.")) {
      input.checked =
        filters.values[input.name]?.includes(input.value) ?? false;
      const label =
        input.closest("label") ??
        root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      label?.classList.toggle("active", input.checked);
    } else if (input.name === "filter.v.price.gte")
      input.value =
        filters.minPrice == null ? "" : String(filters.minPrice / 100);
    else if (input.name === "filter.v.price.lte")
      input.value =
        filters.maxPrice == null ? "" : String(filters.maxPrice / 100);
    else if (input.name === "sort_by")
      input.value =
        url.searchParams.get("sort_by") ??
        nativeHtml.querySelector('select[name="sort_by"]')?.value ??
        input.value;
  }
  for (const detail of root.querySelectorAll("details.js-filter")) {
    const name = detail.querySelector('input[name^="filter."]')?.name;
    if (!name || name.startsWith(`${PRICE}.`)) continue;
    const definition = definitions.find(
      (definition) => definition.paramName === name,
    );
    const selected = filters.values[name]?.length ?? 0;
    detail
      .querySelector("summary")
      ?.setAttribute("aria-label", `${definition?.label ?? name}, ${selected}`);
    for (const label of detail.querySelectorAll(".facets__selected")) {
      if (label.closest("summary")) {
        label.textContent = `(${selected})`;
        label.classList.toggle("hidden", selected === 0);
      } else label.textContent = `${selectedLabel} ${selected}`;
    }
  }
  for (const link of root.querySelectorAll("facet-remove a")) {
    if (link.closest("[data-general-pill]")) continue;
    const target = new URL(url);
    target.searchParams.delete("page");
    const name = link
      .closest("details")
      ?.querySelector('input[name^="filter."]')?.name;
    for (const key of [...target.searchParams.keys()]) {
      if (!key.startsWith("filter.")) continue;
      if (
        !name ||
        (name.startsWith(`${PRICE}.`)
          ? key.startsWith(`${PRICE}.`)
          : key === name)
      )
        target.searchParams.delete(key);
    }
    link.href = target.href;
  }
  const selections = Object.entries(filters.values).flatMap(([name, values]) =>
    values.map((value) => {
      const definition = definitions.find(
        (definition) => definition.paramName === name,
      );
      const label =
        definition?.values.find((candidate) => candidate.value === value)
          ?.label ?? value;
      const target = new URL(url);
      target.searchParams.delete("page");
      target.searchParams.delete(name, value);
      return { label: `${definition?.label ?? name}: ${label}`, url: target };
    }),
  );
  if (filters.minPrice != null || filters.maxPrice != null) {
    const target = new URL(url);
    target.searchParams.delete("page");
    target.searchParams.delete(`${PRICE}.gte`);
    target.searchParams.delete(`${PRICE}.lte`);
    selections.push({
      label: `${filters.minPrice == null ? "" : filters.minPrice / 100} – ${filters.maxPrice == null ? "" : filters.maxPrice / 100}`,
      url: target,
    });
  }
  for (const bucket of root.querySelectorAll(
    ".active-facets-mobile,.active-facets-desktop",
  )) {
    bucket
      .querySelectorAll("[data-general-pill]")
      .forEach((node) => node.remove());
    for (const selection of selections) {
      const wrapper = new DOMParser().parseFromString(
        "<facet-remove><a><span></span></a></facet-remove>",
        "text/html",
      ).body.firstElementChild;
      wrapper.dataset.generalPill = "";
      const link = wrapper.querySelector("a");
      link.href = selection.url.href;
      link.className = "active-facets__button active-facets__button--light";
      const span = link.querySelector("span");
      span.className = "active-facets__button-inner button button--tertiary";
      span.textContent = `${selection.label} ×`;
      bucket.append(wrapper);
    }
  }
}

export function registerGeneralCatalog(root) {
  if (
    root.dataset.generalConnected ||
    !root.querySelector("#ProductGridContainer")
  )
    return;
  root.dataset.generalConnected = "true";
  let surfacesMounted = false;
  let controller,
    generation = 0,
    debounceTimer,
    desiredUrl = new URL(location.href);
  const section = root.dataset.generalSection;
  const status = root.querySelector("[data-general-status]");
  const retry = root.querySelector("[data-general-retry]");
  const controls = () => [
    ...root.querySelectorAll("facet-filters-form,facet-remove,menu-drawer"),
  ];
  const clearNativeLoading = () =>
    root
      .querySelectorAll(".loading")
      .forEach((node) => node.classList.remove("loading"));
  async function apply(input, push = false) {
    const current = ++generation;
    controller?.abort();
    controller = new AbortController();
    const connection = controller;
    const deadline = setTimeout(
      () => connection.abort(new Error("Catalog budget")),
      20000,
    );
    const budget = catalogReadBudget({ signal: connection.signal });
    const url = new URL(input);
    url.searchParams.delete("section_id");
    desiredUrl = url;
    status.textContent = status.dataset.loading;
    root.dataset.generalState = "loading";
    root.setAttribute("aria-busy", "true");
    retry.hidden = true;
    retry.classList.add("hidden");
    const grid = root.querySelector("#ProductGridContainer");
    grid.inert = true;
    grid.hidden = true;
    if (push) history.pushState({}, "", url);
    try {
      const nativeUrl = new URL(url);
      nativeUrl.searchParams.delete("page");
      nativeUrl.searchParams.set("section_id", section);
      const baseUrl = baseCatalogUrl(nativeUrl);
      const readPage = async (pageUrl) =>
        parseNativePage(await budget.read(pageUrl));
      const selections = new Map();
      const loadFamily = async (metadata, pageUrl) => {
        const familyUrl = new URL(metadata.representativeUrl, location.origin);
        if (
          familyUrl.origin !== location.origin ||
          !familyUrl.pathname.includes("/products/")
        )
          throw new Error("Family context");
        familyUrl.search = pageUrl.search;
        for (const name of [...familyUrl.searchParams.keys()])
          if (
            name.startsWith("filter.") ||
            ["page", "q", "type", "sort_by", "section_id"].includes(name)
          )
            familyUrl.searchParams.delete(name);
        familyUrl.searchParams.set(
          "section_id",
          "iamvaclav-four-newcase-general-index",
        );
        if (!selections.has(familyUrl.href))
          selections.set(
            familyUrl.href,
            (async () => {
              const document = new DOMParser().parseFromString(
                await budget.read(familyUrl),
                "text/html",
              );
              const node = document.querySelector("[data-family-index]");
              if (!node) throw new Error("Incomplete family index");
              return JSON.parse(node.textContent);
            })(),
          );
        const selection = await selections.get(familyUrl.href);
        if (
          selection.familyId !== metadata.familyId ||
          !selection.familyProduct
        )
          throw new Error("Family identity");
        return { ...metadata, ...selection };
      };
      const [native, base] = await Promise.all([
        collectCatalogStream(nativeUrl, {
          readPage,
          loadFamily,
          signal: connection.signal,
        }),
        collectCatalogStream(baseUrl, {
          readPage,
          loadFamily,
          signal: connection.signal,
        }),
      ]);
      const filters = decodeCatalogFilters(url);
      const definitions = base.definitions;
      const merged = mergeGeneralResults(
        native.items,
        base.items,
        base.families,
        filters,
        url.searchParams.get("sort_by"),
        definitions,
      );
      const nativeCounts = [];
      for (const definition of definitions) {
        let current = native.definitions.find(
          (value) => value.paramName === definition.paramName,
        );
        if (!current) {
          const probeUrl = new URL(nativeUrl);
          for (const name of [...probeUrl.searchParams.keys()])
            if (
              name === definition.paramName ||
              name.startsWith(`${definition.paramName}.`)
            )
              probeUrl.searchParams.delete(name);
          const probe = await readPage(probeUrl);
          current = probe.definitions.find(
            (value) => value.paramName === definition.paramName,
          );
          if (!current && probe.total !== 0)
            throw new Error("Unverified native facet counts");
        }
        nativeCounts.push({
          ...definition,
          rangeMax: current?.rangeMax ?? 0,
          rangeMaxFormatted: current?.rangeMaxFormatted,
          values: definition.values.map((value) => ({
            ...value,
            count:
              current?.values.find(
                (candidate) => candidate.value === value.value,
              )?.count ?? 0,
          })),
        });
      }
      const counts = correctFamilyFacetCounts(
        nativeCounts,
        base.families,
        filters,
        definitions,
      );
      const pageSize = Number(root.dataset.generalPageSize);
      const pages = Math.max(1, Math.ceil(merged.length / pageSize));
      const selectedPage = Math.min(
        pages,
        Math.max(1, Number(url.searchParams.get("page")) || 1),
      );
      const fresh = base.html.querySelector("[data-general-catalog]");
      if (!fresh || !pageSize) throw new Error("Missing native section");
      const cards = merged
        .slice((selectedPage - 1) * pageSize, selectedPage * pageSize)
        .map((item) =>
          item.family ? updateFamilyCard(item) : item.card.cloneNode(true),
        );
      const list = document.createElement("ul");
      list.className =
        root.querySelector("#product-grid ul,#product-grid.grid")?.className ??
        "grid product-grid grid--2-col-tablet-down grid--4-col-desktop";
      list.id = "product-grid";
      list.dataset.id = section;
      list.replaceChildren(...cards);
      list
        .querySelectorAll(".scroll-trigger")
        .forEach((node) =>
          node.classList.remove("scroll-trigger", "animate--slide-in"),
        );
      const pagination = document.createElement("nav");
      pagination.className = "pagination-wrapper page-width";
      pagination.setAttribute("aria-label", status.dataset.page);
      for (const [number, label] of [
        [selectedPage - 1, status.dataset.previous],
        [selectedPage + 1, status.dataset.next],
      ]) {
        if (number < 1 || number > pages) continue;
        const link = document.createElement("a");
        const target = new URL(url);
        target.searchParams.set("page", String(number));
        link.href = target.href;
        link.textContent = label;
        link.dataset.generalPage = String(number);
        link.className = "button button--secondary";
        pagination.append(link);
      }
      const pageLabel = document.createElement("p");
      pageLabel.textContent = `${status.dataset.page} ${selectedPage} / ${pages}`;
      pagination.prepend(pageLabel);
      if (
        current !== generation ||
        !root.isConnected ||
        connection.signal.aborted
      )
        return;
      if (!surfacesMounted) {
        for (const id of [
          "FacetSortForm",
          "main-collection-filters",
          "main-search-filters",
        ]) {
          const old = root.querySelector(`#${id}`),
            replacement = fresh.querySelector(`#${id}`);
          if (!replacement) {
            old?.remove();
            continue;
          }
          if (old) {
            const drawer = old.querySelector("menu-drawer");
            if (drawer?.querySelector("details[open]"))
              drawer.closeMenuDrawer(new Event("catalog-refresh"));
            old.replaceWith(replacement);
          } else if (id === "FacetSortForm")
            grid.parentElement.before(
              replacement.closest("facet-filters-form"),
            );
          else grid.before(replacement);
        }
        surfacesMounted = true;
      }
      hydrateFacetControls(
        root,
        url,
        filters,
        definitions,
        native.html,
        status.dataset.selected,
      );
      correctFacetDom(
        root,
        counts,
        merged.length,
        native.currency,
        native.locale,
      );
      root
        .querySelectorAll("[data-general-native-empty]")
        .forEach((node) => (node.hidden = true));
      if (root.classList.contains("template-search"))
        root.classList.toggle("template-search--empty", merged.length === 0);
      grid.replaceChildren(list, pagination);
      grid.hidden = false;
      grid.inert = false;
      status.textContent = merged.length
        ? `${status.dataset.results} ${merged.length}`
        : status.dataset.empty;
      if (pages > 1)
        status.textContent += `, ${status.dataset.page} ${selectedPage} / ${pages}`;
      root.dataset.generalMetrics = JSON.stringify({
        ...budget.metrics,
        results: merged.length,
        families: base.families.length,
      });
      root.dataset.generalState = "ready";
      clearNativeLoading();
    } catch (error) {
      if (current === generation && root.isConnected) {
        connection.abort();
        root.dataset.generalState = "error";
        status.textContent = status.dataset.error;
        retry.hidden = false;
        retry.classList.remove("hidden");
      }
    } finally {
      clearTimeout(deadline);
      if (current === generation) {
        root.removeAttribute("aria-busy");
        controls().forEach((node) => (node.inert = false));
      }
    }
  }
  root.addEventListener(
    "input",
    (event) => {
      const input = event.target;
      if (!input.name?.startsWith("filter.") && input.name !== "sort_by")
        return;
      event.stopImmediatePropagation();
      const url = new URL(desiredUrl);
      url.searchParams.delete("page");
      const scope = input.form ?? root;
      if (input.type === "checkbox") {
        url.searchParams.delete(input.name);
        for (const checkbox of scope.querySelectorAll('input[type="checkbox"]'))
          if (checkbox.name === input.name && checkbox.checked)
            url.searchParams.append(input.name, checkbox.value);
      } else if (input.name.startsWith(`${PRICE}.`)) {
        const value = input.value.replaceAll(/\s/g, "").replace(",", ".");
        if (value) url.searchParams.set(input.name, value);
        else url.searchParams.delete(input.name);
      } else url.searchParams.set(input.name, input.value);
      // Abort immediately. The debounce only delays the replacement generation.
      generation++;
      controller?.abort();
      clearTimeout(debounceTimer);
      desiredUrl = url;
      root.querySelector("#ProductGridContainer").hidden = true;
      status.textContent = status.dataset.loading;
      root.dataset.generalState = "loading";
      root.setAttribute("aria-busy", "true");
      debounceTimer = setTimeout(() => apply(url, true), 250);
    },
    true,
  );
  root.addEventListener(
    "change",
    (event) => {
      if (
        event.target.name?.startsWith("filter.") ||
        event.target.name === "sort_by"
      )
        event.stopImmediatePropagation();
    },
    true,
  );
  root.addEventListener(
    "submit",
    (event) => {
      if (!event.target.closest("facet-filters-form")) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );
  root.addEventListener(
    "click",
    (event) => {
      const link = event.target.closest(
        "facet-remove a,.pagination a,[data-general-page]",
      );
      if (
        !link ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const url = new URL(link.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname)
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      clearTimeout(debounceTimer);
      apply(url, true);
    },
    true,
  );
  root.addEventListener(
    "keydown",
    (event) => {
      if (event.code === "Space" && event.target.closest("facet-remove a"))
        event.preventDefault();
    },
    true,
  );
  root.addEventListener(
    "keyup",
    (event) => {
      const link = event.target.closest("facet-remove a");
      if (!link || event.code !== "Space") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      clearTimeout(debounceTimer);
      apply(link.href, true);
    },
    true,
  );
  window.addEventListener(
    "popstate",
    (event) => {
      if (root.isConnected) {
        event.stopImmediatePropagation();
        clearTimeout(debounceTimer);
        apply(location.href);
      }
    },
    true,
  );
  retry.addEventListener("click", () => apply(location.href));
  controls().forEach((node) => (node.inert = true));
  apply(location.href);
}
if (typeof document !== "undefined") {
  document
    .querySelectorAll("[data-general-catalog]")
    .forEach(registerGeneralCatalog);
  document.addEventListener("shopify:section:load", (event) =>
    event.target
      .querySelectorAll("[data-general-catalog]")
      .forEach(registerGeneralCatalog),
  );
}
